import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import type { DocumentSectionsResponse, SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import { getTextRowsWithCalculatedSubtotals } from '../../components/editor/GridDndEditor/Block/Table/tableUtils/calculateSubTotalsPerRow.ts';
import { updateTotalRowsWithSubtotalCalculation } from '../../components/editor/GridDndEditor/Block/Table/tableUtils/updateTotalRowsWithSubtotalCalculation.ts';
import { DEFAULT_DECIMAL_PLACES, setOfRowsForPricingTable } from '../../components/editor/GridDndEditor/Block/Table/defaultData.tsx';
import type { TableColumnType } from '../../components/editor/grid/reduxStore/table.types.ts';
import { TableTypeIdentifier } from '../../components/editor/grid/reduxStore/table.types.ts';
import { GridBlockType } from 'services/entities/BlockTypes';
import { getEngineRowsWithCalculatedSubtotals } from '../../components/editor/GridDndEditor/Block/Table/tableUtils/getEngineRowsWithCalculatedSubtotals.ts';
import { addDecimals } from '@proposify/pricing-engine';
import type { BillingFrequency, FrequencyTotals } from '@proposify/pricing-engine';

type FrequencyAccumulator = {
  totals: Partial<Record<BillingFrequency, string>>;
  present: Set<BillingFrequency>;
  hasEngineFallback: boolean;
};

interface SectionsContextProps {
  sections: DocumentSectionsResponse;
  sectionsQueryStatus: string;
  updateSections: (newSections: DocumentSectionsResponse) => void;
  activeSectionId?: string;
  setActiveSectionId: (newSectionId: string) => void;
  documentFrequencyTotals: FrequencyTotals | null;
  documentPresentFrequencies: BillingFrequency[];
}

const SectionsContext = createContext<SectionsContextProps | undefined>(undefined);

const updateSection = (section: SectionByDocumentIdApiResponse, frequencyAccumulator: FrequencyAccumulator) => {
  return {
    ...section,
    blocks: section.blocks.map((block) => {
      if (block.type === GridBlockType.TABLE && block.content?.rows) {
        const columns = block.content.columns as TableColumnType[];
        const pricingDecimalPlaces = block.content.metadata?.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES;
        let sumOfSubtotalColumn: number;
        try {
          const engineResult = getEngineRowsWithCalculatedSubtotals(block.content.rows, columns, undefined, pricingDecimalPlaces);
          sumOfSubtotalColumn = Number(engineResult.sumOfSubtotalColumn);
          engineResult.presentFrequencies.forEach((frequency) => frequencyAccumulator.present.add(frequency));
          for (const [frequency, total] of Object.entries(engineResult.frequencyTotals ?? {}) as [BillingFrequency, string][]) {
            const runningTotal = frequencyAccumulator.totals[frequency];
            frequencyAccumulator.totals[frequency] = runningTotal ? addDecimals(runningTotal, total) : total;
          }
        } catch (error) {
          console.error('Pricing calculation error, falling back to legacy totals:', error);
          frequencyAccumulator.hasEngineFallback = true;
          sumOfSubtotalColumn = getTextRowsWithCalculatedSubtotals(block.content.rows, columns).sumOfSubtotalColumn;
        }
        const totalRows = block.content?.totalRows;
        const defaultTotalRows = setOfRowsForPricingTable;
        const updatedTotalRows = updateTotalRowsWithSubtotalCalculation(totalRows || defaultTotalRows, sumOfSubtotalColumn);
        return {
          ...block,
          content: {
            ...block.content,
            totalRows: updatedTotalRows,
            rows: block.content.rows,
            metadata: {
              ...(block.content.metadata || {
                tableType: TableTypeIdentifier.TEXT_TABLE,
                pricingDecimalPlaces: DEFAULT_DECIMAL_PLACES,
              }),
              tableTotal: updatedTotalRows[updatedTotalRows.length - 1]?.subtotal,
            },
          },
        };
      }
      return block;
    }),
  };
};

// Pure recompute — usable outside this provider's state/render cycle.
export const calculateSectionsWithSubtotals = (
  newSections: DocumentSectionsResponse
): { sections: DocumentSectionsResponse; frequencyAccumulator: FrequencyAccumulator } => {
  const frequencyAccumulator: FrequencyAccumulator = { totals: {}, present: new Set(), hasEngineFallback: false };
  const calculatedSections = newSections?.sections?.map((section) => updateSection(section, frequencyAccumulator)) || [];
  return { sections: { sections: calculatedSections }, frequencyAccumulator };
};

export const SectionsProvider: React.FC<{
  children: ReactNode;
  sectionData: DocumentSectionsResponse | undefined;
  sectionsQueryStatus: 'error' | 'success' | 'pending';
}> = ({ children, sectionData, sectionsQueryStatus }) => {
  const [sections, setSections] = useState<DocumentSectionsResponse>({} as DocumentSectionsResponse);
  const [activeSectionId, setActiveSectionId] = useState<string>();
  const [documentFrequencyTotals, setDocumentFrequencyTotals] = useState<FrequencyTotals | null>(null);
  const [documentPresentFrequencies, setDocumentPresentFrequencies] = useState<BillingFrequency[]>([]);

  useEffect(() => {
    if (sectionData && sectionsQueryStatus === 'success') {
      updateSections(sectionData);
    }
  }, [sectionData, sectionsQueryStatus]);

  const updateSections = (newSections: DocumentSectionsResponse) => {
    const { sections: calculatedSections, frequencyAccumulator } = calculateSectionsWithSubtotals(newSections);
    setSections(calculatedSections);
    const isFrequencyDataReliable = !frequencyAccumulator.hasEngineFallback && frequencyAccumulator.present.size > 0;
    setDocumentFrequencyTotals(isFrequencyDataReliable ? (frequencyAccumulator.totals as FrequencyTotals) : null);
    setDocumentPresentFrequencies(isFrequencyDataReliable ? [...frequencyAccumulator.present] : []);
  };

  return (
    <SectionsContext.Provider
      value={{
        sections,
        sectionsQueryStatus,
        updateSections,
        activeSectionId,
        setActiveSectionId,
        documentFrequencyTotals,
        documentPresentFrequencies,
      }}
    >
      {children}
    </SectionsContext.Provider>
  );
};

export const useSectionsForPreview = (): SectionsContextProps => {
  const context = useContext(SectionsContext);
  if (!context) {
    throw new Error('useSections must be used within a SectionsProvider');
  }
  return context;
};
